import { Suspense, lazy } from "react";
import type { RouteObject } from "react-router";

import type { PromptBlockRefQuery } from "@app_schema";

import type { Breadcrumb } from "@application/types/breadcrumb";

import { DASHBOARD_PROMPT_BLOCK_REF_LOADER } from "@application/loaders/dashboard_prompt_block_ref_loader";
import { DASHBOARD_PROMPT_BLOCKS_LIST_LOADER } from "@application/loaders/dashboard_prompt_blocks_list_loader";

import { DASHBOARD_PROMPTS_LINK } from "@application/navigation/dashboard_prompts_link";

import { Loader } from "@styled/loader";

import { promptBlockPath, promptTemplatePath } from "./prompt_block_path";

const DashboardPromptBlocksList = lazy(() =>
  import("./prompt_blocks_list").then((m) => ({
    default: m.DashboardPromptBlocksList,
  })),
);
const DashboardPromptBlocksNew = lazy(() =>
  import("./prompt_blocks_new").then((m) => ({
    default: m.DashboardPromptBlocksNew,
  })),
);
const DashboardPromptBlockDetails = lazy(() =>
  import("./prompt_block_details").then((m) => ({
    default: m.DashboardPromptBlockDetails,
  })),
);
const DashboardPromptBlockVersionForm = lazy(() =>
  import("./prompt_block_version_form").then((m) => ({
    default: m.DashboardPromptBlockVersionForm,
  })),
);

const TEMPLATE_HANDLE = (data: PromptBlockRefQuery): Breadcrumb => ({
  name: data.promptBlock.title,
  to: promptTemplatePath(data.promptBlock),
});

const VERSION_HANDLE = (data: PromptBlockRefQuery): Breadcrumb => ({
  name: data.promptBlock.label,
  to: promptBlockPath(data.promptBlock),
});

const suspend = (element: React.ReactNode) => (
  <Suspense fallback={<Loader />}>{element}</Suspense>
);

export const DASHBOARD_PROMPTS_ROUTE = {
  path: "prompts",
  handle: DASHBOARD_PROMPTS_LINK,
  children: [
    {
      index: true,
      element: suspend(<DashboardPromptBlocksList />),
      loader: DASHBOARD_PROMPT_BLOCKS_LIST_LOADER,
    },
    {
      path: "new",
      element: suspend(<DashboardPromptBlocksNew />),
    },
    {
      path: ":name",
      loader: DASHBOARD_PROMPT_BLOCK_REF_LOADER,
      handle: TEMPLATE_HANDLE,
      children: [
        {
          index: true,
          element: suspend(<DashboardPromptBlockDetails />),
        },
        {
          path: "versions/new",
          element: suspend(<DashboardPromptBlockVersionForm />),
        },
        {
          path: ":version",
          loader: DASHBOARD_PROMPT_BLOCK_REF_LOADER,
          handle: VERSION_HANDLE,
          children: [
            {
              index: true,
              element: suspend(<DashboardPromptBlockDetails />),
            },
            {
              path: "targets",
              element: suspend(<DashboardPromptBlockVersionForm />),
            },
          ],
        },
      ],
    },
  ],
} satisfies RouteObject;
